/**
 * RichTextEditor — Tiptap, matching digitaladmin's major version (2.x) so the
 * shared design language holds. A small fixed toolbar (bold/italic/underline/
 * lists/link) over StarterKit, Link and Placeholder. Emits sanitised-on-write
 * HTML — the SERVER still sanitises (lib/sanitize.ts); this only shapes what
 * a human can produce, it is not the security boundary.
 */
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { toast } from 'sonner';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Underline as UnderlineIcon,
  Baseline,
} from 'lucide-react';
import { useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
import { usePrompt } from '../../lib/confirm';

/**
 * A base64 `data:` image URI as a File, so it can ride the same upload as a
 * pasted screenshot — Step 190. Returns null for anything unparseable.
 */
function dataUriToFile(dataUri: string): File | null {
  const m = /^data:(image\/[a-z0-9.+-]+);base64,(.+)$/i.exec(dataUri);
  if (!m) return null;
  try {
    const bin = atob(m[2]!);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const ext = (m[1]!.split('/')[1] ?? 'png').split('+')[0];
    return new File([bytes], `pasted.${ext}`, { type: m[1]! });
  } catch {
    return null;
  }
}

function ToolbarButton({
  active,
  onClick,
  label,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onMouseDown={(e) => e.preventDefault()} // keep editor selection/focus on click
      onClick={onClick}
      className={cn(
        'flex h-7 w-7 items-center justify-center rounded transition-colors',
        active
          ? 'bg-accent text-accent-foreground'
          : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 120,
  tinted,
  surface = 'agent',
  caretToken,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  tinted?: boolean;
  /**
   * Step 112 — which media door a pasted image goes through. The portal must
   * never call the agent endpoint; three auth domains, never crossed.
   */
  surface?: 'agent' | 'portal';
  /**
   * Step 177 — put the caret on the first EMPTY line, and focus.
   *
   * Kai: *"how does it decide where the text auto starts?"* By the blank
   * paragraph, which is exactly what the template editor tells an admin to
   * leave. The alternative — recording where the caret happened to be when the
   * template was saved, as his reference product does — stores a character
   * offset into HTML, and any later edit to the template silently moves it
   * somewhere daft. A blank line is visible, survives editing, and means the
   * same thing to the person writing the template and the person using it.
   *
   * Pass a value that CHANGES when the caret should move (the ticket id, say).
   * It is not "focus on mount": the composer is opened deliberately, and
   * stealing focus at any other moment would fight whoever is already typing.
   */
  caretToken?: string | number | null;
}) {
  const prompt = usePrompt();
  /** The paste handler is installed INTO the editor, so it cannot close over
   *  `editor` — the binding does not exist yet at that point. A ref does. */
  const editorRef = useRef<ReturnType<typeof useEditor> | null>(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }), // a reply is not an article
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Placeholder.configure(placeholder ? { placeholder } : {}),
      /**
       * Step 112 — inline images.
       *
       * An agent explaining something pastes a screenshot; a requester
       * reporting a fault does the same. Without this the paste silently
       * produced nothing, which reads as the editor being broken.
       *
       * `allowBase64: false` is the load-bearing part. A pasted image arrives
       * as a data: URI by default, which would embed megabytes of base64 into
       * the message body — bloating the row, the email and every future read
       * of the thread, and bypassing the upload validation entirely. Uploading
       * it and inserting a `/api/media/<id>` reference instead means it rides
       * the same magic-byte sniffing, size cap, quota and per-owner
       * authorisation as every other file.
       */
      Image.configure({ allowBase64: false, HTMLAttributes: { class: 'rounded-md border' } }),
      // Step 190 — keep colour. StarterKit has no colour node, so a pasted
      // branded signature (Kai's orange) lost it; these two parse and hold
      // `<span style="color:…">`, which the sanitiser already allows through.
      TextStyle,
      Color,
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        // `prose-strong:text-inherit` — Step 191. Tailwind Typography colours
        // bold text with its own `--tw-prose-bold`, which OVERRODE an applied
        // colour: a name written `<span style="color:orange"><strong>…` showed
        // white, because `.prose strong` won over the parent span (Kai). Making
        // bold inherit its colour is right anyway — bold is emphasis, not a
        // colour — and it lets a coloured-and-bold run keep its colour.
        // `overflow-x-auto` — Step 202. Editing an email body with a
        // fixed-width table (every marketing template has one) pushed the
        // whole page sideways instead of scrolling inside the editor.
        class:
          'prose prose-sm dark:prose-invert max-w-none overflow-x-auto focus:outline-none prose-strong:text-inherit',
      },
      // Paste and drop both land here. Returning false lets Tiptap handle
      // anything that is not an image, so ordinary text paste is untouched.
      handlePaste: (_view, event) => {
        // A pasted image FILE (a screenshot) — upload and insert it.
        if (uploadFromDataTransfer(event.clipboardData)) return true;
        // A pasted rich fragment whose images are inline `data:` base64 — a
        // signature copied from a mail client, say (Kai). Tiptap and the
        // sanitiser both drop `data:`, so the images vanish; pull them out,
        // upload them, and insert the fragment pointing at the stored copies.
        const html = event.clipboardData?.getData('text/html');
        // `data:` images we can upload; `cid:` images we cannot — they point
        // INTO the email they were copied from, and the paste carries the
        // reference, never the bytes (Kai, pasting an Outlook signature). Both
        // go through the same handler: one uploads, the other is dropped with a
        // word about why, so nothing lands as a broken image.
        if (html && (html.includes('data:image/') || /src\s*=\s*["']?cid:/i.test(html))) {
          void pasteHtmlUploadingImages(html);
          return true;
        }
        return false;
      },
      handleDrop: (_view, event) => uploadFromDataTransfer((event as DragEvent).dataTransfer),
    },
  });

  /**
   * Upload any images in a paste or drop, then insert them.
   *
   * Inserted only AFTER the upload succeeds: showing a local preview first and
   * swapping the src later means a failed upload leaves a broken image in a
   * message somebody then sends. A refusal is told to the person instead.
   */
  /**
   * Upload one image and return the stored id, or null on refusal. Shared by
   * the file-paste path and the inline-`data:`-in-HTML path.
   *
   * `kb_image` is the metadata-STRIPPING purpose: an image we publish, not
   * evidence somebody attached. A screenshot pasted from a phone must not carry
   * its GPS into a customer's inbox. Bare fetch on purpose: a multipart body
   * must reach the network untouched so the browser sets its own boundary, and
   * the endpoint differs per surface so the portal never calls an agent route.
   */
  async function uploadImage(file: File): Promise<string | null> {
    const body = new FormData();
    body.append('file', file);
    body.append('purpose', 'kb_image');
    const res = await fetch(surface === 'portal' ? '/api/portal/media' : '/api/media', {
      method: 'POST',
      body,
      credentials: 'same-origin',
    });
    const json = (await res.json().catch(() => null)) as { id?: string; error?: string } | null;
    if (!res.ok || !json?.id) {
      toast.error(json?.error ?? 'That image could not be uploaded.');
      return null;
    }
    return json.id;
  }

  function uploadFromDataTransfer(dt: DataTransfer | null): boolean {
    const images = [...(dt?.files ?? [])].filter((f) => f.type.startsWith('image/'));
    if (!images.length) return false;
    void (async () => {
      for (const file of images) {
        const id = await uploadImage(file);
        if (id)
          editorRef.current
            ?.chain()
            .focus()
            .setImage({ src: `/api/media/${id}` })
            .run();
      }
    })();
    return true; // handled — do not let Tiptap embed the raw file
  }

  /**
   * Paste a rich fragment whose images are inline `data:` URIs: upload each,
   * rewrite the fragment to point at the stored copies, and insert it. The
   * whole fragment is inserted (not just the images), so the text and its order
   * survive — returning true from handlePaste means Tiptap will not also insert
   * it, so we own the whole thing.
   */
  async function pasteHtmlUploadingImages(html: string): Promise<void> {
    // Parse the fragment rather than regex the string: a mail client wraps
    // base64 across lines and quotes attributes however it likes, and a regex
    // over the raw HTML captured a truncated URI and produced a corrupt upload.
    // The DOM gives each `src` back whole and normalised.
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const imgs = [...doc.querySelectorAll('img')].filter((el) =>
      (el.getAttribute('src') ?? '').startsWith('data:image/'),
    );
    // Upload the DISTINCT images once, then point every copy at the result.
    const byUri = new Map<string, string>();
    for (const el of imgs) {
      const uri = el.getAttribute('src') ?? '';
      if (!byUri.has(uri)) {
        const file = dataUriToFile(uri);
        const id = file ? await uploadImage(file) : null;
        byUri.set(uri, id ? `/api/media/${id}` : '');
      }
      const url = byUri.get(uri);
      if (url) el.setAttribute('src', url);
      else el.remove(); // upload refused — drop the broken tag, not leave it
    }
    // `cid:` images cannot be recovered: they reference the email they were
    // copied from, and the paste never carried the bytes. Drop them so nothing
    // lands broken, and say why once — the fix is to add the real files.
    const cidImgs = [...doc.querySelectorAll('img')].filter((el) =>
      (el.getAttribute('src') ?? '').toLowerCase().startsWith('cid:'),
    );
    for (const el of cidImgs) el.remove();
    if (cidImgs.length) {
      toast.message('Images copied from an email were left out', {
        description:
          'They live inside that email, not on the clipboard. Drag the image files in, or paste their web address.',
      });
    }
    editorRef.current?.chain().focus().insertContent(doc.body.innerHTML).run();
  }

  editorRef.current = editor;

  /**
   * Take content that arrives AFTER mount.
   *
   * This used to sync only when `value` became `''` — clearing a draft after
   * send — which meant any form that mounted the editor before its data
   * loaded showed an empty box over stored content. A published incident
   * review rendered completely blank (Kai), and the real danger was the next
   * step: pressing Save would have written that blank back over it.
   *
   * `isFocused` is the guard that keeps this from fighting the typist, and
   * `false` is Tiptap v2's "do not emit an update" — without it every load
   * would mark the form dirty and trip the unsaved-changes prompt on a form
   * nobody had touched. `ArticleEditor` has had exactly this since Step 33;
   * this editor simply never got it.
   */
  useEffect(() => {
    if (!editor || editor.isFocused) return;
    if (value === editor.getHTML()) return;
    editor.commands.setContent(value || '', false);
  }, [value, editor]);

  /**
   * Step 177 — put the caret where the template left a gap.
   *
   * Walks the document for the first empty paragraph and selects it. Falls
   * back to the end, which is the sensible answer for a template with no gap
   * in it — better than the start, where typing would land in front of the
   * greeting.
   *
   * Runs only when `caretToken` changes, so it cannot steal focus from
   * somebody mid-sentence.
   */
  useEffect(() => {
    if (!editor || caretToken === null || caretToken === undefined) return;
    let target: number | null = null;
    editor.state.doc.descendants((node, pos) => {
      if (target !== null) return false;
      if (node.type.name === 'paragraph' && node.content.size === 0) target = pos + 1;
      return true;
    });
    editor
      .chain()
      .focus()
      .setTextSelection(target ?? editor.state.doc.content.size)
      .run();
    // `value` is deliberately absent: the caret moves when the CALLER says so,
    // not every time the text changes — which would be every keystroke.
  }, [caretToken, editor]);

  if (!editor) return null;

  return (
    <div
      className={cn(
        'rounded-md border border-input bg-background shadow-sm focus-within:ring-2 focus-within:ring-ring',
        tinted && 'border-amber-500/50 focus-within:ring-amber-500',
      )}
    >
      <div className="flex items-center gap-0.5 border-b px-1.5 py-1">
        <ToolbarButton
          label="Bold"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label="Underline"
          active={false}
          onClick={() => editor.chain().focus().toggleMark('underline').run()}
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
        <span className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton
          label="Bullet list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>
        <span className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton
          label="Link"
          active={editor.isActive('link')}
          onClick={async () => {
            const url = await prompt({
              title: 'Link',
              label: 'Address',
              placeholder: 'https://',
              initial: (editor.getAttributes('link').href as string | undefined) ?? '',
              confirmLabel: 'Link it',
            });
            // Cancel leaves the link alone; an empty box clears it. The
            // browser's prompt made those the same gesture.
            if (url === null) return;
            if (url.trim())
              editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
            else editor.chain().focus().unsetLink().run();
          }}
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
        {/* Step 190 — text colour. The native picker is a `label` wrapping a
            hidden colour input, so the whole swatch is the target; the current
            colour shows on the glyph so you can see what is set. */}
        <label
          className="relative flex h-7 w-7 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
          title="Text colour"
        >
          <Baseline
            className="h-3.5 w-3.5"
            style={{ color: (editor.getAttributes('textStyle').color as string) || undefined }}
          />
          <input
            type="color"
            aria-label="Text colour"
            className="absolute inset-0 cursor-pointer opacity-0"
            value={(editor.getAttributes('textStyle').color as string) || '#000000'}
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          />
        </label>
        {editor.getAttributes('textStyle').color && (
          <ToolbarButton label="Clear colour" onClick={() => editor.chain().focus().unsetColor().run()}>
            <span className="text-[10px] font-semibold">A×</span>
          </ToolbarButton>
        )}
      </div>
      {/**
       * `minHeight` has to reach the CONTENTEDITABLE, not just this wrapper.
       *
       * It was on the wrapper alone, so the box LOOKED 120px tall while the
       * ProseMirror element inside it was only as tall as its content — 24px
       * when empty. Clicking anywhere in the lower three quarters of an empty
       * editor hit the wrapper and did nothing at all, which reads exactly as
       * "I can't type in this box" (Kai, on the resolution notes — three
       * empty editors in a column is where it becomes obvious, but every
       * rich-text field in the app had it).
       *
       * `flex` + `[&>*]:flex-1` makes the editable child fill whatever height
       * the wrapper is given, so the whole visible box is the click target.
       */}
      <EditorContent editor={editor} className="flex flex-col px-3 py-2 [&>*]:flex-1" style={{ minHeight }} />
    </div>
  );
}
